import { useEffect, useState } from "react";
import { Apple, Play } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  goExternal,
  CUSTOMER_STORE_EVENT,
} from "@/lib/links";

/**
 * Global "Choose your store" dialog. Mounted once at the landing-page root.
 * Any CTA can trigger it via `window.dispatchEvent(new Event(CUSTOMER_STORE_EVENT))`
 * — which is exactly what `goCustomer()` does for desktop visitors.
 */
export default function CustomerStoreDialog() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(CUSTOMER_STORE_EVENT, handler);
    return () => window.removeEventListener(CUSTOMER_STORE_EVENT, handler);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="customer-store-dialog"
        className="max-w-md border-black/10 bg-[#F7F5F2] text-[#2A2724] p-0 overflow-hidden"
      >
        {/* Gold hairline top */}
        <div className="h-[3px] w-full bg-gradient-to-r from-[#C9A85F] via-[#E6D3A3] to-[#C9A85F]" />

        <div className="px-8 pb-8 pt-7">
          <p className="overline text-gold mb-3">Book Away</p>
          <DialogTitle className="font-serif text-3xl font-semibold leading-tight text-[#2A2724]">
            Get the app.
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm leading-relaxed text-[#6B6B6B]">
            Openings near you, on demand. Choose your store to download Book
            Away.
          </DialogDescription>

          <div className="mt-7 grid gap-3">
            <button
              data-testid="store-dialog-appstore-btn"
              onClick={() => {
                goExternal(APP_STORE_URL);
                setOpen(false);
              }}
              className="group flex items-center justify-between rounded-2xl border border-black/15 bg-[#2A2724] px-5 py-4 text-left text-[#F7F5F2] transition-colors duration-300 hover:border-gold"
            >
              <span className="flex items-center gap-4">
                <Apple className="h-7 w-7" strokeWidth={1.5} />
                <span className="flex flex-col leading-tight">
                  <span className="text-[0.65rem] uppercase tracking-[0.24em] text-[#F7F5F2]/60">
                    Download on the
                  </span>
                  <span className="font-serif text-lg font-medium">
                    App Store
                  </span>
                </span>
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                Open →
              </span>
            </button>

            <button
              data-testid="store-dialog-playstore-btn"
              onClick={() => {
                goExternal(PLAY_STORE_URL);
                setOpen(false);
              }}
              className="group flex items-center justify-between rounded-2xl border border-black/15 bg-[#2A2724] px-5 py-4 text-left text-[#F7F5F2] transition-colors duration-300 hover:border-gold"
            >
              <span className="flex items-center gap-4">
                <Play
                  className="h-7 w-7 fill-[#F7F5F2] text-[#F7F5F2]"
                  strokeWidth={1.5}
                />
                <span className="flex flex-col leading-tight">
                  <span className="text-[0.65rem] uppercase tracking-[0.24em] text-[#F7F5F2]/60">
                    Get it on
                  </span>
                  <span className="font-serif text-lg font-medium">
                    Google Play
                  </span>
                </span>
              </span>
              <span className="text-xs uppercase tracking-[0.2em] text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                Open →
              </span>
            </button>
          </div>

          <p className="mt-6 text-[0.7rem] uppercase tracking-[0.24em] text-[#6B6B6B]/80">
            Available in Australia · Coming soon
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
